import {
  type V2AdminClientAccountDetailDto,
  type V2AdminClientAccountEventsPageDto,
  type V2AdminClientAccountsPageDto,
  type V2AdminClientAccountsSummaryDto,
  v2ClientAccountAdminControllerBlock,
  v2ClientAccountAdminControllerCreate,
  v2ClientAccountAdminControllerDetail,
  v2ClientAccountAdminControllerEvents,
  v2ClientAccountAdminControllerGrant,
  v2ClientAccountAdminControllerList,
  v2ClientAccountAdminControllerReactivate,
  v2ClientAccountAdminControllerResendInvitation,
  v2ClientAccountAdminControllerRevokeAllSessions,
  v2ClientAccountAdminControllerRevokeGrant,
  v2ClientAccountAdminControllerRevokeSession,
  v2ClientAccountAdminControllerSummary,
  v2ClientAccountAdminControllerUpdateEmail
} from '@idc-forms/api-client'
import {
  V2AdminClientAccountDetailSchema,
  V2AdminClientAccountEventsPageSchema,
  V2AdminClientAccountsPageSchema,
  V2AdminClientAccountsSummarySchema
} from '@idc-forms/schemas/api-v2/admin-client-accounts'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Administración de las cuentas de la app móvil contra la API v2.
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae el `querySerializer` con
 * bracket notation. El serializer por defecto del SDK usa `deepObject` de UN solo nivel y
 * revienta en runtime ante cualquier filtro anidado del ADR 0020 — que acá son todos,
 * porque los filtros útiles de esta pantalla son `$null` sobre fechas.
 *
 * Toda LECTURA pasa por `assertShape`: el contrato es la única garantía de que la tabla
 * no está pintando `undefined` con forma de dato.
 */

/**
 * Los tipos que consumen los componentes salen del SDK GENERADO, no de
 * `packages/schemas`.
 *
 * No es un detalle: el SSOT usa ids y fechas *branded* (`cac_${string}` con marca de
 * marca) mientras el SDK los publica como `string` pelado. Los dos describen el mismo
 * wire, pero mezclarlos hace que un componente tipado contra el schema no acepte lo que
 * el servicio declara devolver. La ola entera se tipa contra el SDK (ver
 * `models/project-admin-v2.ts`) y ésta también.
 */
export type ClientAccountsAdminV2Row = V2AdminClientAccountsPageDto['data'][number]
export type ClientAccountDetailV2 = V2AdminClientAccountDetailDto
export type ClientAccountGrantV2 = ClientAccountDetailV2['grants'][number]
export type ClientAccountDeviceV2 = ClientAccountDetailV2['devices'][number]
export type ClientAccountEventV2 = ClientAccountDetailV2['events'][number]

export type ClientAccountsAdminQuery = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

function toListQuery(query: ClientAccountsAdminQuery) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const clientAccountsAdminV2Service = {
  list: async (
    accessToken: string,
    query: ClientAccountsAdminQuery
  ): Promise<V2AdminClientAccountsPageDto> => {
    const result = await v2ClientAccountAdminControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminClientAccountsPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<ClientAccountsAdminQuery, 'filters' | 'search'>
  ): Promise<V2AdminClientAccountsSummaryDto> => {
    const result = await v2ClientAccountAdminControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminClientAccountsSummarySchema, result.data)
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminClientAccountDetailDto> => {
    const result = await v2ClientAccountAdminControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminClientAccountDetailSchema, result.data)
  },

  /** Una página más VIEJA de la bitácora: `before` es el `nextCursor` de la anterior. */
  events: async (
    accessToken: string,
    id: string,
    query: { before: string; limit?: number }
  ): Promise<V2AdminClientAccountEventsPageDto> => {
    const result = await v2ClientAccountAdminControllerEvents({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      query
    })
    return assertShape(V2AdminClientAccountEventsPageSchema, result.data)
  },

  create: async (accessToken: string, body: { clientId: string; email: string }) => {
    const result = await v2ClientAccountAdminControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  updateEmail: async (accessToken: string, id: string, email: string) => {
    const result = await v2ClientAccountAdminControllerUpdateEmail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: { email }
    })
    return result.data
  },

  resendInvitation: async (accessToken: string, id: string) => {
    const result = await v2ClientAccountAdminControllerResendInvitation({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return result.data
  },

  grant: async (
    accessToken: string,
    id: string,
    body: { propertyId: string; expiresAt?: string }
  ) => {
    const result = await v2ClientAccountAdminControllerGrant({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  revokeGrant: async (accessToken: string, id: string, grantId: string): Promise<void> => {
    await v2ClientAccountAdminControllerRevokeGrant({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id, grantId }
    })
  },

  revokeSession: async (accessToken: string, id: string, sessionId: string): Promise<void> => {
    await v2ClientAccountAdminControllerRevokeSession({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id, sessionId }
    })
  },

  revokeAllSessions: async (accessToken: string, id: string): Promise<void> => {
    await v2ClientAccountAdminControllerRevokeAllSessions({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  },

  block: async (accessToken: string, id: string) => {
    const result = await v2ClientAccountAdminControllerBlock({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return result.data
  },

  reactivate: async (accessToken: string, id: string) => {
    const result = await v2ClientAccountAdminControllerReactivate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return result.data
  }
}
